import { Component, DestroyRef, inject, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { EmployeeModel } from '@reducers/orm/employee/employee.model';
import { DepartmentOptionsPerLocation } from '@reducers/orm/location/location.model';
import { PermissionGroupModel } from '@reducers/orm/permission-group/permission-group.model';
import { TeamModel } from '@reducers/orm/team/team.model';
import { mgcIconAlertRegular } from '@sb/mgc-icons/mgcIcon-alert_regular.icon';
import { KnowledgeBaseArticleComponent } from '@sb/shared/knowledge-base';
import { SectionToolbarComponent } from '@sb/shared/layout/primitives';
import { TooltipDirective } from '@sb/tooltip';
import { CheckboxChangeEvent, CheckboxComponent } from '@sb/ui/checkbox';
import { LoadingStateComponent } from '@sb/ui/feedback';
import { IconComponent, IconRegistry } from '@sb/ui/icon';
import { InputSelectDirective } from '@sb/ui/input';
import {
  TableCellPrimitiveDirective,
  TableContainerDirective,
  TableDirective,
  TableHeaderCellDirective,
  TableRowDirective,
  TableStickyColumnDirective,
} from '@sb/ui/table';
import every from 'lodash-es/every';
import filter from 'lodash-es/filter';
import forEach from 'lodash-es/forEach';

import { trackByIdFn, trackByLocationFn } from '../../../../shared/trackby';
import { EmployeePermissionFormService } from './employee-permissions-form.service';

@Component({
  selector: 'employee-permissions-teams',
  templateUrl: './employee-permissions-teams.component.html',
  providers: [EmployeePermissionFormService],
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    KnowledgeBaseArticleComponent,
    TooltipDirective,
    CheckboxComponent,
    LoadingStateComponent,
    IconComponent,
    InputSelectDirective,
    TableContainerDirective,
    TableDirective,
    TableRowDirective,
    TableHeaderCellDirective,
    TableCellPrimitiveDirective,
    TableStickyColumnDirective,
    SectionToolbarComponent,
  ],
})
export class EmployeePermissionsTeamsComponent implements OnChanges, OnDestroy {
  #formService = inject(EmployeePermissionFormService);
  #destroyRef = inject(DestroyRef);

  constructor() {
    inject(IconRegistry).registerIcons([mgcIconAlertRegular]);
  }

  loading = true;

  @Input()
  employee: EmployeeModel;
  @Input()
  groups: PermissionGroupModel[];
  @Input()
  locations: DepartmentOptionsPerLocation[];
  @Input()
  teamsPerDepartment: { [departmentId: string]: TeamModel[] };

  @Input()
  editOnlyTeamDepartments: string[];

  @Input()
  form;

  locationGroupForm: UntypedFormGroup;

  trackById = trackByIdFn;
  trackByLocation = trackByLocationFn;

  /**
   * watch for changes on UsersGroup to set The toggle status of the location UsersGroup combination
   */
  #subscribeToDepartmentForm(): void {
    this.form
      .get('Department')
      .valueChanges.pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        // select raw to also get value from disabled fields
        const departmentsFormData = this.form.get('Department').getRawValue();
        forEach(this.locations, (locationOption) => {
          const location = locationOption.location;
          //grab all departmentIds for current location
          const departmentIds = locationOption.departments.map((department) => department.id);

          //get UsersGroups status for location departments
          const departmentValues = filter(
            departmentsFormData,
            (departmentForm: any) => departmentIds.indexOf(departmentForm.id) !== -1,
          ).map((departmentForm: any) => departmentForm.UsersGroup);

          //toggle status for each group
          forEach(this.groups, (group) => {
            const allSelected = every(departmentValues, (departmentGroupStatus) => departmentGroupStatus[group.id]);

            //Update toggle status
            this.locationGroupForm.get([location.id, group.id]).setValue(allSelected);
          });
        });
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.loading && changes['employee'] && !changes['employee'].isFirstChange()) {
      this.#resetForm();
    }

    if (this.loading && this.groups && this.groups.length && this.locations && this.locations.length) {
      this.#initForm();
    }
  }

  #initForm(): void {
    this.locationGroupForm = this.#formService.locationGroupForm(this.locations, this.groups);
    this.#formService.createForm(this.form, this.locations, this.groups);

    this.#subscribeToDepartmentForm();

    this.#resetForm();

    this.loading = false;
  }

  #resetForm(): void {
    this.#formService.setEmployeeData(this.form, this.employee, this.teamsPerDepartment);

    // departments where the user can only edit teams and not set permissions should disable permission selection
    this.editOnlyTeamDepartments?.forEach((departmentId) => {
      this.form.get(['Department', departmentId, 'UserGroups']).disable({ onlySelf: false });
      this.form.get(['Department', departmentId, 'UsersGroup']).disable({ onlySelf: false });
    });
  }

  ngOnDestroy(): void {
    this.form.removeControl('Department');
  }

  toggleGroupForLocation(locationId: string, groupId: string, event: CheckboxChangeEvent): void {
    const selected = event.checked;
    const selectedLocation = this.locations.find((location) => location.location.id === locationId);

    selectedLocation.departments.forEach((department) => {
      const control = this.form.get(['Department', department.id, 'UsersGroup', groupId]) as UntypedFormControl;
      if (control.disabled) {
        return;
      }
      control.setValue(selected, { emitEvent: false });
      control.markAsDirty();
    });

    this.form.updateValueAndValidity();
  }
}
